import { memo, useMemo } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
// import MenuCard from './MenuCard';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

const APP_VERSION = import.meta.env.VITE_APP_VERSION;
const APP_BUILD_TIME = import.meta.env.VITE_APP_BUILD_TIME;
const FORMATTED_BUILD_TIME = APP_BUILD_TIME
  ? new Date(APP_BUILD_TIME).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })
  : null;

// ==============================|| SIDEBAR DRAWER ||============================== //

function Sidebar() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { menuOrientation, miniDrawer, mode } = useConfig();

  const logo = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          p: 2,
          ...(!downMD && {
            minHeight: 83,
          }),
        }}
      >
        <LogoSection />
      </Box>
    ),
    [downMD],
  );

  const drawer = useMemo(() => {
    const isVerticalOpen = menuOrientation === MenuOrientation.VERTICAL && drawerOpen;

    const buildInfo = isVerticalOpen && (APP_VERSION || FORMATTED_BUILD_TIME) && (
      <Stack
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {APP_VERSION && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
            גרסה {APP_VERSION}
          </Typography>
        )}
        {FORMATTED_BUILD_TIME && (
          <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', display: 'block' }}>
            בנייה {FORMATTED_BUILD_TIME}
          </Typography>
        )}
      </Stack>
    );

    let drawerSX = { paddingLeft: '0px', paddingRight: '0px', marginTop: '20px' };
    if (drawerOpen) drawerSX = { paddingLeft: '16px', paddingRight: '16px', marginTop: '0px' };

    return (
      <>
        {downMD ? (
          <Box sx={{ ...drawerSX, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <MenuList />
            {buildInfo}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 93px)' }}>
            <PerfectScrollbar
              style={{ flex: 1, ...drawerSX, marginTop: 5 }}
              options={{
                suppressScrollX: true,
                wheelPropagation: false,
              }}
            >
              <MenuList />
            </PerfectScrollbar>
            {buildInfo}
          </Box>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMD, drawerOpen, menuOrientation, mode]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: { xs: 'auto', md: drawerWidth } }}
      aria-label="mailbox folders"
    >
      {downMD || (miniDrawer && drawerOpen) ? (
        <Drawer
          variant={downMD ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          sx={{
            '& .MuiDrawer-paper': {
              mt: downMD ? 0 : 11,
              zIndex: 1099,
              width: drawerWidth,
              bgcolor: 'background.default',
              color: 'text.primary',
              borderRight: 'none',
            },
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {downMD && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {logo}
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

export default memo(Sidebar);
